import { PageMasterMode, usePageContext } from "../../../../components/navigation/PageContext";

import {
  useMeetingSelector
} from "@kalyzee/kast-web-sdk";
import {
  audioContextIsReadyToUse,
  useRefAutoUpdate
} from "@kalyzee/kast-web-sdk/web-components";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toastError } from "../../../../helpers/toast";
import { buildMediasFromSources, MeetingSourceExtended, useMeetingConfigurator, useMeetingContext, useMeetingSources } from "../../../../hooks/meeting";

import { RTCStreamSenderParamsPreset } from "@kalyzee/kast-web-sdk/webrtc";
import { getAudioContextForVolumeAnalyzer } from "../../../../helpers/audio";
import { DEFAULT_MAX_BITRATES } from "../../../../helpers/videos";

import { MeetingContainer } from "@kalyzee/kast-web-sdk/contexts/meeting/core/container";
import { SessionMode } from "../../../../contexts/appStore/hooks";
import { useAppStoreSelector } from "../../../../contexts/appStore/selectors";
import { useAppStoreDispatch } from "../../../../contexts/appStore/context";

import { LoadingView } from "../../../../components/utils/LoadingView";
import { MeetingContainerProvider } from "../../../../contexts/meetingContainer/provider";
import { MeetingUtilsProvider } from "../../../../contexts/meetingUtils/provider";
import { useSettings } from "../../../../hooks/settings";
import { useDelayedState } from "../../../../hooks/states";
import { ConnectedMeetingPage } from "./connected/base";
import { MeetingLobbyPage } from "./lobby";

type Phase = "loading" | "lobby" | "joining" | "connected";

export const MeetingInitPage = () => {
  const pageContext = usePageContext<SessionMode.MASTER>();
  const pageContextRef = useRefAutoUpdate(pageContext);

  const audioEnabled = useAppStoreSelector(state => state.audioStreamEnabled);
  const videoEnabled = useAppStoreSelector(state => state.videoStreamEnabled);
  const sourcePreferences = useAppStoreSelector(state => state.sourcePreferences);

  const sources = useMeetingSources();

  const dispatch = useAppStoreDispatch();

  const meetingCtx = useMeetingContext();

  const [settings] = useSettings();
  const settingsRef = useRefAutoUpdate(settings);

  const meetingsAPI = meetingCtx?.api;
  const meetingsAPIRef = useRefAutoUpdate(meetingsAPI);

  const [phase, setPhase] = useState<Phase>("loading");

  const container = useMemo(() => {
    if (!meetingsAPI) return undefined;

    const containers = meetingsAPI.getContainers();
    const selectedId = pageContextRef.current?.params?.id;

    if (selectedId) {
      return containers.find((c) => c.getMeetingId() === selectedId);
    }

    return containers[0];
  }, [meetingsAPI]);

  // Avoids flickering
  const [showMeeting] = useDelayedState(container !== undefined, 1000);

  const joinContainer = useCallback(async (container: MeetingContainer) => {
    if (container.getStatus() !== "disconnected") return;

    const containerAPI = container.getAPI();

    const medias = buildMediasFromSources(sources as MeetingSourceExtended[], {
      getSourceEnabled: (sourceName) => {
        const pref = sourcePreferences[sourceName];
        return {
          audio: pref?.audioEnabled ?? audioEnabled,
          video: pref?.videoEnabled ?? videoEnabled,
        };
      },
    });

    const result = await containerAPI.join({
      medias,
    });

    if (result.error) {
      console.error(`[ERROR] join: `, result.error);
      toastError(`Une erreur est apparue.`);
      pageContextRef.current?.goTo(PageMasterMode.LIST, undefined, { queries: { meeting: undefined } });
      container.destroy();
      return;
    }
    const session = result.response?.session;
    if (!session) {
      console.error(`[ERROR] join - internal error: session is undefined`);
      toastError(`Une erreur est apparue.`);
      pageContextRef.current?.goTo(PageMasterMode.LIST, undefined, { queries: { meeting: undefined } });
      container.destroy();
      return;
    }

    setPhase("connected");
  }, [audioEnabled, videoEnabled, sourcePreferences, sources, dispatch]);

  const joinTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const createAndJoinContainer = useCallback(async (id: string) => {
    const api = meetingsAPIRef.current;

    if (!api) {
      console.error(`[ERROR] join - meetingContext is undefined `);
      toastError(`Une erreur est apparue.`);
      pageContextRef.current?.goTo(PageMasterMode.LIST, undefined, { queries: { meeting: undefined } });
      return;
    }

    joinTimeoutRef.current = setTimeout(() => {
      const container = api.getOrCreateContainer(id);
      joinContainer(container);
    }, 0);
  }, [joinContainer]);

  const leave = async () => {
    const promises: Promise<any>[] = [];
    meetingsAPIRef.current?.getContainers()?.forEach((c) => {
      const result = c.getAPI()?.leave();
      if (!("error" in result)) promises.push(result);
    });
    await Promise.allSettled(promises);
  };

  useMeetingConfigurator(container?.getAPI(), {
    ovenMediaSession: {
      emission: {
        options: (mediaSession) => {
          if (mediaSession.name === "screen") {
            const maxBitrate = settingsRef.current?.debug?.maxBitrate?.student?.screensharing ?? DEFAULT_MAX_BITRATES.student.screensharing;
            return {
              rtc: {
                streamParams: {
                  preset: RTCStreamSenderParamsPreset.VISIO_SCREENSHARING,
                  video: { rtcEncoding: { maxBitrate: maxBitrate?.video, networkPriority: "low" } },
                  audio: { rtcEncoding: { maxBitrate: maxBitrate?.audio, networkPriority: "low" } },
                },
              },
            };
          }
          const maxBitrate = settingsRef.current?.debug?.maxBitrate?.student?.webcam ?? DEFAULT_MAX_BITRATES.student.webcam;
          return {
            rtc: {
              streamParams: {
                preset: RTCStreamSenderParamsPreset.VISIO_FACE,
                video: { rtcEncoding: { maxBitrate: maxBitrate?.video, networkPriority: "low" } },
                audio: { rtcEncoding: { maxBitrate: maxBitrate?.audio, networkPriority: "low" } },
              },
            },
          };
        },
      },
      reception: {
        options: () => ({
          video: { mode: "subscription", delayToDisableStream: 5000 },
          audio: true,
        }),
      },
    },
  });

  // Phase 1: test audio context
  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      // Test audio context — if ready (came from user gesture), skip lobby
      const ready = await audioContextIsReadyToUse(getAudioContextForVolumeAnalyzer());
      if (cancelled) return;

      if (ready) {
        setPhase("joining");
        return;
      }

      // Audio context not ready — show lobby for user interaction
      setPhase("lobby");
    };

    init();

    return () => {
      cancelled = true;
      if (joinTimeoutRef.current) clearTimeout(joinTimeoutRef.current);
      leave();
    };
  }, []);

  // Phase: joining → create container and join
  useEffect(() => {
    if (phase !== "joining") return;
    const id = pageContextRef.current?.params?.id ?? "";
    createAndJoinContainer(id);
  }, [phase]);

  const handleMediaConfirm = useCallback(async () => {
    // The click is the user gesture — ensure audio context is unlocked
    await audioContextIsReadyToUse(getAudioContextForVolumeAnalyzer());
    setPhase("joining");
  }, []);

  if (phase === "loading") {
    return <LoadingView fullscreen message="Chargement du meeting ..." />;
  }

  if (phase === "lobby") {
    return <MeetingLobbyPage onConfirm={handleMediaConfirm} />;
  }

  if (phase === "joining" || !container || !showMeeting) {
    return <LoadingView fullscreen message="Connexion au meeting ..." />;
  }

  return (
    <MeetingContainerProvider container={container}>
      <MeetingUtilsProvider>
        <ConnectedMeetingPage />
      </MeetingUtilsProvider>
    </MeetingContainerProvider>
  );
};
